/** 
 * DISCOUNT_WELDING_LandingPage_v020b_WIZARD (React + TypeScript + React-Bootstrap)
 * Version: v020b
 * Date: 2025-09-02 (America/Chicago)
 *
 * Updates vs v020:
 * - Renamed labels:
 *   • "Annual payroll" -> "Employee Payroll"
 *   • "Subcontracted Work (%)" -> "Subcontractor Annual Cost"
 * - All submitted fields are STRINGS. No numeric coercion; backend receives text.
 * - Minimal validation only (presence + light email format).
 * - Confirmation labels mirror the new field names.
 */

import React, { useMemo, useRef, useState, ReactNode } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  OverlayTrigger, Tooltip,
  Nav,
  Navbar,
  Row,
  Spinner,
  InputGroup,  
  ProgressBar,
  Carousel,
} from "react-bootstrap";

import { FAQ_Component_v02b_Simple } from "../../component-library/FAQ_Component_v02b-simple";
import { WELDING_INSURANCE_FAQ_CONTENT_v01 } from "../../Discount-Welding-2025/WELDING_INSURANCE_FAQ_CONTENT_v01";


// import { FAQ_Component_v02b_Simple } from "../../component-library/FAQ_Component_v02b-simple";

/* ============================================================
   CONFIG
============================================================ */
const VERSION = "v020Ob";
const SHOW_VERSION = false;
const TEST_MODE = false; // true => logs payload, skips network
const API_URL = "https://wi8zkeven2.execute-api.us-east-1.amazonaws.com/prod/feedback";
const AGENT_PHONE_NUMBER = "(303) 337-4433";
const imagePath = "insurance/images/WELDING-Insurance/";
        
const FormSection_SubTitle_CoverageSafety: ReactNode  = <p>&nbsp;&nbsp;Your agent will help update and find the best answers to this section</p>

/* ============================================================
   TYPES
============================================================ */
type Deductible = "" | "0" | "250" | "500" | "1000" | "2500";
type WorkEnv = "" | "shop" | "mobile" | "both";
type WeldProcess = "mig" | "tig" | "stick" | "flux_core" | "oxy_fuel" | "spot" | "other";
type HeightLevel = "" | "ground_only" | "up_to_two_stories" | "over_two_stories";

/** Raw lead payload. NOTE: everything that can be text is a string. */
interface ILeadPayload {
  // Contact & Business
  fullName: string;
  email: string;
  phone: string;

  businessName: string;
  yearsInBusiness: string;         // stringified
  employeesCount: string;          // stringified
  website?: string;

  // Address
  address1: string;
  address2?: string;
  city: string;
  state: string;
  zipCode: string;

  // Operations & Exposures
  workEnv: WorkEnv;
  processes: WeldProcess[];
  materials: string[];
  industries: string[];
  heightWork: HeightLevel;
  confinedSpaces: "yes" | "no";
  usesCuttingTorch: "yes" | "no";
  mobileRadiusMiles?: string;
  // Keep this for future toggles if needed:
  usesSubcontractors?: "yes" | "no";

  annualRevenue: string;
  employeePayroll: string;           // renamed
  subcontractorAnnualCost: string;   // renamed

  // Coverage & Safety
  glPerOccurrence: string;
  glAggregate: string;
  toolsLimit: string;
  inlandMarine: "yes" | "no";
  commercialAuto: "yes" | "no";
  workersComp: "yes" | "no";
  deductible: Deductible;
  additionalInsureds: string;      // stringified count
  needCOIs: "yes" | "no";

  oshaTraining: "yes" | "no";
  ppeEnforced: "yes" | "no";
  ventilationControls: "yes" | "no";
  hotWorkPermits: "yes" | "no";
  fireWatch: "yes" | "no";

  priorClaimsCount5y: string;      // stringified count
  lastClaimDate?: string;

  // Notes / Files
  notes?: string;
  fileUrls?: string;

  // Meta
  domainName: string;
  itemCategory: string;
  itemClass: string;
  itemType: string;
  clientVersion: string;

  utm?: { source?: string; medium?: string; campaign?: string };
}

/** Wire payload where EVERY VALUE IS A STRING */
type WirePayload = Record<string, string>;

/* ============================================================
   HELPERS
============================================================ */
const US_STATES: { code: string; name: string }[] = [
  { code: "AL", name: "Alabama" }, { code: "AK", name: "Alaska" }, { code: "AZ", name: "Arizona" },
  { code: "AR", name: "Arkansas" }, { code: "CA", name: "California" }, { code: "CO", name: "Colorado" },
  { code: "CT", name: "Connecticut" }, { code: "DE", name: "Delaware" }, { code: "FL", name: "Florida" },
  { code: "GA", name: "Georgia" }, { code: "HI", name: "Hawaii" }, { code: "ID", name: "Idaho" },
  { code: "IL", name: "Illinois" }, { code: "IN", name: "Indiana" }, { code: "IA", name: "Iowa" },
  { code: "KS", name: "Kansas" }, { code: "KY", name: "Kentucky" }, { code: "LA", name: "Louisiana" },
  { code: "ME", name: "Maine" }, { code: "MD", name: "Maryland" }, { code: "MA", name: "Massachusetts" },
  { code: "MI", name: "Michigan" }, { code: "MN", name: "Minnesota" }, { code: "MS", name: "Mississippi" },
  { code: "MO", name: "Missouri" }, { code: "MT", name: "Montana" }, { code: "NE", name: "Nebraska" },
  { code: "NV", name: "Nevada" }, { code: "NH", name: "New Hampshire" }, { code: "NJ", name: "New Jersey" },
  { code: "NM", name: "New Mexico" }, { code: "NY", name: "New York" }, { code: "NC", name: "North Carolina" },
  { code: "ND", name: "North Dakota" }, { code: "OH", name: "Ohio" }, { code: "OK", name: "Oklahoma" },
  { code: "OR", name: "Oregon" }, { code: "PA", name: "Pennsylvania" }, { code: "RI", name: "Rhode Island" },
  { code: "SC", name: "South Carolina" }, { code: "SD", name: "South Dakota" }, { code: "TN", name: "Tennessee" },
  { code: "TX", name: "Texas" }, { code: "UT", name: "Utah" }, { code: "VT", name: "Vermont" },
  { code: "VA", name: "Virginia" }, { code: "WA", name: "Washington" }, { code: "WV", name: "West Virginia" },
  { code: "WI", name: "Wisconsin" }, { code: "WY", name: "Wyoming" },
];

const PROCESS_LABELS: Record<WeldProcess, string> = {
  mig: "MIG",
  tig: "TIG",
  stick: "Stick (SMAW)",
  flux_core: "Flux-Core",
  oxy_fuel: "Oxy-Fuel",
  spot: "Spot",
  other: "Other",
};

const money = (nLike?: string) => {
  const digits = (nLike ?? "").replace(/[^\d.]/g, "");
  const n = Number(digits || "0");
  if (!Number.isFinite(n) || n <= 0) return "—";
  try {
    return n.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 0 });
  } catch {
    return `$${n}`;
  }
};
function isValidEmail(email: string): boolean {
  return /.+@.+\..+/.test(email);
}
function dash(v?: string | number | boolean | null) {
  if (v === undefined || v === null) return "—";
  const s = String(v).trim();
  return s.length ? s : "—";
}

/* ============================================================
   SLIDES
============================================================ */
interface Slide { src: string; alt: string; title: ReactNode; subline: string; smallCaption: string; }

const slides: Slide[] = [ 
  {
    src: imagePath + "AdobeStock_58042709.jpg",
    alt: "Welder working with sparks in workshop.",
    title: "Protect Your Welding Business",
    subline: "General liability, tools & equipment, and more—at a discount.",
    smallCaption: "Fast quotes. Clear coverage.",
  },
  {
    src: imagePath + "AdobeStock_169195418.jpg",
    alt: "Mobile welder truck and equipment.",
    title: "Shop or Mobile—We’ve Got You",
    subline: "Cover field work, hot work permits, and jobsite risks.",
    smallCaption: "Flexible limits.",
  },
  {
    src: imagePath + "AdobeStock_320564310.jpg",
    alt: "Welding mask close-up with arc light.",
    title: "Safety First, Better Rates",
    subline: "Rewarded for OSHA training, PPE, and fire watch.",
    smallCaption: "Licensed advisors.",
  },
  {
    src: imagePath + "AdobeStock_485199129.jpg",
    alt: "Welding tools neatly organized in a workshop.",
    title: "Protect Your Tools & Gear",
    subline: "Coverage for torches, welders, grinders, and more.",
    smallCaption: "Don’t pay out-of-pocket for theft or damage.",
  },
  {
    src: imagePath + "AdobeStock_219897880.jpg",
    alt: "Welder on a high-rise construction site.",
    title: "Jobsite Liability Covered",
    subline: "Protection against property damage and third-party injury.",
    smallCaption: "Peace of mind on every project.",
  },
  {
    src: imagePath + "AdobeStock_920377570.jpg",
    alt: "Business owner reviewing documents at desk.",
    title: "Affordable Contractor Policies",
    subline: "Bundle general liability with workers’ comp and auto.",
    smallCaption: "Save more with tailored plans.",
  },
  {
    src: imagePath + "AdobeStock_1413656862.jpg",
    alt: "Modern welder wearing helmet with sparks flying.",
    title: <>Stay Compliant,<br/> Stay Covered & <br/>protect your business</>,
    subline: "Meet insurance requirements for permits and contracts.",
    smallCaption: "Approved coverage that keeps you working.",
  },
  {
    src: imagePath + "AdobeStock_786649127.jpg",
    alt: "Welder shaking hands with a contractor at site.",
    title: "Support for Small & Large Crews",
    subline: "From solo welders to full teams—we scale with you.",
    smallCaption: "Coverage that grows as you grow.",
  },
];

/* ============================================================
   MAIN
============================================================ */
export const DISCOUNT_WELDING_LandingPage_v020b_WIZARD: React.FC = () => {
  const formRef = useRef<HTMLDivElement | null>(null);
  const [activeSlide, setActiveSlide] = useState(0);

  // Contact & Business (strings)
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [yearsInBusiness, setYearsInBusiness] = useState<string>("0");
  const [employeesCount, setEmployeesCount] = useState<string>("0");
  const [website, setWebsite] = useState<string>("");

  // Address (strings)
  const [address1, setAddress1] = useState("");
  const [address2, setAddress2] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [zipCode, setZipCode] = useState("");

  // Operations & Exposures
  const [workEnv, setWorkEnv] = useState<WorkEnv>("");
  const [processes, setProcesses] = useState<WeldProcess[]>([]);
  const [materials, setMaterials] = useState<string[]>([]);
  const [industries, setIndustries] = useState<string[]>([]);
  const [heightWork, setHeightWork] = useState<HeightLevel>("");
  const [confinedSpaces, setConfinedSpaces] = useState<"yes" | "no">("no");
  const [usesCuttingTorch, setUsesCuttingTorch] = useState<"yes" | "no">("yes");
  const [mobileRadiusMiles, setMobileRadiusMiles] = useState<string>("");

  // Simple money-ish strings
  const [annualRevenue, setAnnualRevenue] = useState<string>("150000");
  const [employeePayroll, setEmployeePayroll] = useState<string>("80000");               // renamed
  const [subcontractorAnnualCost, setSubcontractorAnnualCost] = useState<string>("0");   // renamed

  // Coverage & Safety (strings + yes/no)
  const [glPerOccurrence, setGlPerOccurrence] = useState<string>("1000000");
  const [glAggregate, setGlAggregate] = useState<string>("2000000");
  const [toolsLimit, setToolsLimit] = useState<string>("10000");
  const [inlandMarine, setInlandMarine] = useState<"yes" | "no">("yes");
  const [commercialAuto, setCommercialAuto] = useState<"yes" | "no">("no");
  const [workersComp, setWorkersComp] = useState<"yes" | "no">("no");
  const [deductible, setDeductible] = useState<Deductible>("500");
  const [additionalInsureds, setAdditionalInsureds] = useState<string>("0");
  const [needCOIs, setNeedCOIs] = useState<"yes" | "no">("yes");

  const [oshaTraining, setOshaTraining] = useState<"yes" | "no">("yes");
  const [ppeEnforced, setPpeEnforced] = useState<"yes" | "no">("yes");
  const [ventilationControls, setVentilationControls] = useState<"yes" | "no">("yes");
  const [hotWorkPermits, setHotWorkPermits] = useState<"yes" | "no">("yes");
  const [fireWatch, setFireWatch] = useState<"yes" | "no">("yes");

  const [priorClaimsCount5y, setPriorClaimsCount5y] = useState<string>("0");
  const [lastClaimDate, setLastClaimDate] = useState<string>("");

  // Notes / Files
  const [notes, setNotes] = useState<string>("");
  const [fileUrls, setFileUrls] = useState<string>("");

  // Submit / server state
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitOk, setSubmitOk] = useState(false);
  const [serverStatus, setServerStatus] = useState<null | { ok: boolean; status?: number; id?: string; message?: string }>(null);
  const [lastSubmittedApp, setLastSubmittedApp] = useState<ILeadPayload | null>(null);

  // Mode + wizard
  const [mode, setMode] = useState<"wizard" | "vertical">("wizard");
  const isWizard = mode === "wizard";
  const [currentStep, setCurrentStep] = useState(0);
  const steps = ["Contact & Business", "Operations & Exposures", "Coverage, Safety & Notes", "Confirmation"] as const;

  /* ---------- Minimal validation ---------- */
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const isTouched = (name: string) => !!touched[name];
  const markTouched = (name: string) => setTouched((t) => ({ ...t, [name]: true }));

  const vp = (name: string, pass: boolean) => ({
    onBlur: () => markTouched(name),
    isValid: isTouched(name) && pass,
    isInvalid: isTouched(name) && !pass,
    className: isTouched(name) && pass ? "bg-success-subtle" : undefined,
  });
  const vpOpt = (name: string, valuePresent: boolean, passIfPresent: boolean = true) => ({
    onBlur: () => markTouched(name),
    isValid: isTouched(name) && valuePresent && passIfPresent,
    isInvalid: isTouched(name) && valuePresent && !passIfPresent,
    className: isTouched(name) && valuePresent && passIfPresent ? "bg-success-subtle" : undefined,
  });

  // Keep this intentionally light & simple
  const V = {
    fullName: fullName.trim().length >= 2,
    email: isValidEmail(email),
    phone: phone.trim().length >= 7, // simplified
    businessName: businessName.trim().length >= 2,

    address1: address1.trim().length >= 3,
    city: city.trim().length >= 2,
    state: state !== "",
    zip: zipCode.trim().length >= 3,

    workEnv: workEnv !== "",
    processes: processes.length > 0,
    materials: materials.length > 0,
    industries: industries.length > 0,
    heightWork: heightWork !== "",

    annualRevenue: annualRevenue.trim().length > 0,
    employeePayroll: employeePayroll.trim().length > 0,
    subcontractorAnnualCost: subcontractorAnnualCost.trim().length >= 0, // optional OK

    glPerOccurrence: glPerOccurrence.trim().length > 0,
    glAggregate: glAggregate.trim().length > 0,
    toolsLimit: toolsLimit.trim().length >= 0,
    deductible: deductible !== "",
    additionalInsureds: additionalInsureds.trim().length >= 0,

    priorClaimsCount5y: priorClaimsCount5y.trim().length >= 0,
    lastClaimDate: true, // optional
    notes: true,         // optional
    fileUrls: true,      // optional
    mobileRadius: true,  // optional
  };

  const valid = useMemo(() => {
    return (
      V.fullName && V.email && V.phone &&
      V.businessName &&
      V.address1 && V.city && V.state && V.zip &&
      V.workEnv && V.processes && V.materials && V.industries && V.heightWork &&
      V.annualRevenue && V.employeePayroll &&
      V.glPerOccurrence && V.glAggregate && V.toolsLimit && V.deductible
    );
  }, [V]);

  function isStepValid(stepIdx: number): boolean {
    switch (stepIdx) {
      case 0:
        return V.fullName && V.email && V.phone && V.businessName && V.address1 && V.city && V.state && V.zip;
      case 1:
        return V.workEnv && V.processes && V.materials && V.industries && V.heightWork && V.annualRevenue && V.employeePayroll;
      case 2:
        return V.glPerOccurrence && V.glAggregate && V.toolsLimit && V.deductible;
      case 3:
        return submitOk === true;
      default:
        return false;
    }
  }

  const goNext = () => {
    const touchMany = (names: string[]) => setTouched((t) => names.reduce((acc, n) => ({ ...acc, [n]: true }), t));
    if (currentStep === 0) touchMany(["fullName","email","phone","businessName","address1","city","state","zip"]);
    else if (currentStep === 1) touchMany(["workEnv","processes","materials","industries","heightWork","annualRevenue","employeePayroll"]);
    else if (currentStep === 2) touchMany(["glPerOccurrence","glAggregate","toolsLimit","deductible"]);

    if (!isStepValid(currentStep)) return;
    setCurrentStep((s) => Math.min(3, s + 1));
  };
  const goBack = () => setCurrentStep((s) => Math.max(0, s - 1));

  /* ============================================================
     PAYLOAD & SUBMIT (EVERYTHING => strings)
  ============================================================ */
  function buildLeadPayload(): ILeadPayload {
    return {
      // Contact & Business
      fullName: fullName.trim(),
      email: email.trim(),
      phone: phone.trim(),

      businessName: businessName.trim(),
      yearsInBusiness: yearsInBusiness.trim(),
      employeesCount: employeesCount.trim(),
      website: website?.trim() || "",

      // Address
      address1: address1.trim(),
      address2: address2?.trim() || "",
      city: city.trim(),
      state: String(state),
      zipCode: String(zipCode),

      // Operations
      workEnv,
      processes,
      materials,
      industries,
      heightWork,
      confinedSpaces,
      usesCuttingTorch,
      mobileRadiusMiles: mobileRadiusMiles?.trim() || "",
      // usesSubcontractors not currently shown as a toggle; omit or default
      usesSubcontractors: subcontractorAnnualCost.trim() !== "" ? "yes" : "no",

      // Revenew 
      annualRevenue: annualRevenue.trim(),
      employeePayroll: employeePayroll.trim(),
      subcontractorAnnualCost: subcontractorAnnualCost.trim(),


      // Coverage & Safety
      glPerOccurrence: glPerOccurrence.trim(),
      glAggregate: glAggregate.trim(),
      toolsLimit: toolsLimit.trim(),
      inlandMarine,
      commercialAuto,
      workersComp,
      deductible,
      additionalInsureds: additionalInsureds.trim(),
      needCOIs,

      oshaTraining,
      ppeEnforced,
      ventilationControls,
      hotWorkPermits,
      fireWatch,

      priorClaimsCount5y: priorClaimsCount5y.trim(),
      lastClaimDate: (lastClaimDate || "").trim(),

      // Notes / Files
      notes: notes?.trim() || "",
      fileUrls: fileUrls?.trim() || "",

      // Meta
      domainName: typeof window !== "undefined" ? String(window.location.hostname) : "unknown",
      itemCategory: "Customer.welding.quote",
      itemClass: "quote.request",
      itemType: "insurance.welding.landing",
      clientVersion: String(VERSION),
    };
  }

  function toWirePayloadStrings(p: ILeadPayload): WirePayload {
    const out: WirePayload = {};
    for (const [k, v] of Object.entries(p)) {
      if (typeof v === "string") out[k] = v;
      else if (Array.isArray(v)) {
        try { out[k] = JSON.stringify(v); } catch { out[k] = ""; }
      } else if (v && typeof v === "object") {
        try { out[k] = JSON.stringify(v); } catch { out[k] = ""; }
      } else {
        // should not happen (we normalized booleans to "yes"/"no")
        out[k] = v == null ? "" : String(v);
      }
    }
    return out;
  }

  async function postLead(payload: WirePayload): Promise<{ ok: boolean; status: number; id?: string; message?: string }> {
    const res = await fetch(API_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      mode: "cors",
    });

    let resJson: unknown = null;
    try {
      const txt = await res.text();
      resJson = txt ? JSON.parse(txt) : null;
    } catch {
      resJson = null;
    }

    const asObj = (x: unknown): Record<string, unknown> | null =>
      x && typeof x === "object" && !Array.isArray(x) ? (x as Record<string, unknown>) : null;

    if (!res.ok) {
      const o = asObj(resJson);
      const msg = (o?.error as string) || (o?.message as string) || `Request failed: ${res.status}`;
      return { ok: false, status: res.status, message: msg };
    }

    const o = asObj(resJson);
    const id = (o?.id as string) || (o?.requestId as string) || undefined;
    return { ok: true, status: res.status, id };
  }

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault();

    if (isWizard && currentStep < 2) {
      goNext();
      return;
    }

    setSubmitError(null);
    setSubmitOk(false);
    setServerStatus(null);

    // Light-touch: mark some core fields
    setTouched((t) => ({
      ...t,
      fullName: true, email: true, phone: true,
      businessName: true, address1: true, city: true, state: true, zip: true,
      workEnv: true, processes: true, materials: true, industries: true, heightWork: true,
      annualRevenue: true, employeePayroll: true,
      glPerOccurrence: true, glAggregate: true, toolsLimit: true, deductible: true,
    }));

    if (!valid) {
      setSubmitError("Please complete required fields marked in red.");
      return;
    }

    const appPayload = buildLeadPayload();
    const wirePayload = toWirePayloadStrings(appPayload);

    try {
      setSubmitting(true);

      if (TEST_MODE) {
        console.log("[TEST_MODE] Submitting payload (wire):", wirePayload);
        await new Promise((r) => setTimeout(r, 500));
        const status = { ok: true, status: 200, id: "TEST-" + Math.random().toString(36).slice(2, 8) };
        setServerStatus(status);
        setLastSubmittedApp(appPayload);
        setSubmitOk(true);
        if (isWizard) setCurrentStep(3);
        return;
      }

      const status = await postLead(wirePayload);
      setServerStatus(status);
      if (!status.ok) throw new Error(status.message || "Unknown server error");

      setLastSubmittedApp(appPayload);
      setSubmitOk(true);
      if (isWizard) setCurrentStep(3);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      setSubmitError(msg);
      setSubmitOk(false);
    } finally {
      setSubmitting(false);
    }
  };

  // Clamp active slide index
  const active = slides[Math.min(slides.length - 1, Math.max(0, Number.isFinite(activeSlide) ? activeSlide : 0))];

  /* ------------------------------------------------------------
     UI
  ------------------------------------------------------------ */
  return (
    <div className="bg-body">
      {/* Sticky Topbar */}
      <Navbar expand="md" bg="light" className="border-bottom sticky-top">
        <Container>
          <Navbar.Brand href="#" className="fw-bold">
            Discount <span className="text-primary">Welding</span> Insurance
            {SHOW_VERSION && <Badge bg="secondary" className="ms-2">{VERSION}</Badge>}
          </Navbar.Brand>
          <Navbar.Toggle aria-controls="topbar" />
          <Navbar.Collapse id="topbar" className="justify-content-end">
            <Nav className="align-items-center gap-2">
              <div className="d-none d-md-block text-body-secondary me-2">Speak with an advisor:</div>
              <Button variant="outline-primary" href="tel:3033375434">{AGENT_PHONE_NUMBER}</Button>
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      {/* HERO */}
      <section id="home" className="position-relative overflow-hidden" style={{ minHeight: "760px" }}>
        <div className="position-absolute top-0 start-0 end-0 bottom-0" style={{ zIndex: 0 }} aria-hidden>
          <Carousel
            activeIndex={activeSlide}
            onSelect={(i) => setActiveSlide(typeof i === "number" ? i : 0)}
            controls
            indicators
            fade
            interval={3500}
            className="w-100 h-100"
          >
            {slides.map((s, idx) => (
              <Carousel.Item key={idx} className="w-100 h-100">
                <div
                  role="img"
                  aria-label={s.alt}
                  style={{
                    width: "100%",
                    height: "100%",
                    minHeight: 760,
                    backgroundImage: `linear-gradient(to right, rgba(0,0,0,0.55), rgba(0,0,0,0.25) 40%, rgba(0,0,0,0.10) 60%, rgba(0,0,0,0.00)), url('${s.src}')`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                  }}
                />
                <Carousel.Caption className="text-start" style={{ bottom: "40%" }} />
              </Carousel.Item>
            ))}
          </Carousel>
        </div>

        {/* Foreground content */}
        <Container className="position-relative" style={{ zIndex: 1 }}>
          {/* <Row className="align-items-start g-4 p-5"> */}
          <Row className="align-items-start g-0 g-sm-4 p-0 p-sm-4 p-lg-5">
            <Col lg={6} className="text-white">
              <div className="pe-lg-4">
                <h1 className="display-5 fw-bold" style={{ marginTop: "400px" }}>{active.title}</h1>
                <p className="lead mb-3">{active.subline}</p>
                <div className="d-flex flex-wrap align-items-center gap-2">
                  <Badge bg="light" text="dark">Mobile & Shop</Badge>
                  <Badge bg="light" text="dark">COIs & AI Endorsements</Badge>
                  <Badge bg="light" text="dark">Licensed Advisors</Badge>
                </div>
                <div className="mt-4 d-flex gap-2">
                  <Button variant="outline-light" href="#faq">Common Questions</Button>
                </div>
              </div>
            </Col>

            <Col lg={6}>
              <Card className="shadow-lg" style={{ backdropFilter: "blur(6px)" }}>
                <Card.Header className="bg-white">
                  <div className="d-flex align-items-center justify-content-between">
                    <div>
                      <div className="fw-semibold">Fast, No-Obligation Quote</div>
                      <small className="text-body-secondary">Tell us about your operation for tailored options.</small>
                    </div>
                    <Badge bg="success">Secure</Badge>
                  </div>
                </Card.Header>
                <Card.Body ref={formRef}>
                  {submitError && (
                    <Alert variant="danger" onClose={() => setSubmitError(null)} dismissible>
                      Server: {submitError}
                    </Alert>
                  )}

                  {/* MODE TOGGLE + (Wizard) Stepper */}
                  <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
                    <div className="d-flex align-items-center gap-2">
                      <Form.Check
                        type="switch"
                        id="modeSwitch"
                        label={isWizard ? "Wizard mode" : "Vertical mode"}
                        checked={isWizard}
                        onChange={(e) => {
                          setMode(e.currentTarget.checked ? "wizard" : "vertical");
                          setTouched({});
                          if (submitOk && e.currentTarget.checked) setCurrentStep(3);
                          else setCurrentStep(0);
                        }}
                      />
                      <small className="text-muted">Switch to see all fields in one view.</small>
                    </div>

                    <div style={{ minWidth: 280 }}>
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <Badge bg="secondary">Step {isWizard ? currentStep + 1 : (submitOk ? 2 : 1)} of {isWizard ? 4 : (submitOk ? 2 : 1)}</Badge>
                        <small className="text-muted">{isWizard ? steps[currentStep] : (submitOk ? "Confirmation" : "All fields")}</small>
                      </div>
                      <ProgressBar now={isWizard ? ((currentStep + 1) / 4) * 100 : (submitOk ? 100 : 50)} />
                    </div>
                  </div>

                  {/* FORM */}
                  <Form
                    onSubmit={handleSubmit}
                    noValidate
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && isWizard) {
                        if (currentStep < 2) {
                          e.preventDefault();
                          e.stopPropagation();
                          if (isStepValid(currentStep)) goNext();
                        }
                      }
                    }}
                  >
                    {isWizard ? (
                      <>
                        <Row className="g-3">
                          {currentStep === 0 && (
                            <Col xs={12}>
                              <SectionCard title="Contact & Business">
                                <SectionContactBusiness
                                  values={{
                                    fullName, email, phone,
                                    businessName, yearsInBusiness, employeesCount, website,
                                    address1, address2, city, state, zipCode,
                                  }}
                                  setters={{
                                    setFullName, setEmail, setPhone,
                                    setBusinessName, setYearsInBusiness, setEmployeesCount, setWebsite,
                                    setAddress1, setAddress2, setCity, setState, setZipCode,
                                  }}
                                  V={V}
                                  vp={vp}
                                  vpOpt={vpOpt}
                                />
                              </SectionCard>
                            </Col>
                          )}

                          {currentStep === 1 && (
                            <Col xs={12}>
                              <SectionCard title="Operations & Exposures">
                                <SectionOperations
                                  values={{
                                    workEnv, processes, materials, industries, heightWork,
                                    confinedSpaces, usesCuttingTorch, mobileRadiusMiles,
                                    annualRevenue, employeePayroll, subcontractorAnnualCost,
                                  }}
                                  setters={{
                                    setWorkEnv, setProcesses, setMaterials, setIndustries, setHeightWork,
                                    setConfinedSpaces, setUsesCuttingTorch, setMobileRadiusMiles,
                                    setAnnualRevenue, setEmployeePayroll, setSubcontractorAnnualCost,
                                  }}
                                  V={V}
                                  vp={vp}
                                  vpOpt={vpOpt}
                                />
                              </SectionCard>
                            </Col>
                          )}

                          {currentStep === 2 && (
                            <>
                              <Col xs={12}>
                                <SectionCard title="Coverage & Safety">
                                  {/* <p>&nbsp;&nbsp;Your agent will help update and find the best answers to this section</p> */}
                                  {FormSection_SubTitle_CoverageSafety}
                                  <SectionCoverageSafety
                                    values={{
                                      glPerOccurrence, glAggregate, toolsLimit, inlandMarine, commercialAuto, workersComp,
                                      deductible, additionalInsureds, needCOIs,
                                      oshaTraining, ppeEnforced, ventilationControls, hotWorkPermits, fireWatch,
                                      priorClaimsCount5y, lastClaimDate,
                                    }}
                                    setters={{
                                      setGlPerOccurrence, setGlAggregate, setToolsLimit, setInlandMarine, setCommercialAuto, setWorkersComp,
                                      setDeductible, setAdditionalInsureds, setNeedCOIs,
                                      setOshaTraining, setPpeEnforced, setVentilationControls, setHotWorkPermits, setFireWatch,
                                      setPriorClaimsCount5y, setLastClaimDate,
                                    }}
                                    V={V}
                                    vp={vp}
                                    vpOpt={vpOpt}
                                  />
                                </SectionCard>
                              </Col>
                              <Col xs={12}>
                                <SectionCard title="Notes">
                                  <SectionNotesFiles
                                    values={{ notes, fileUrls }}
                                    setters={{ setNotes, setFileUrls }}
                                    V={V}
                                    vpOpt={vpOpt}
                                  />
                                </SectionCard>
                              </Col>
                            </>
                          )}

                          {currentStep === 3 && (
                            <Col xs={12}>
                              <ConfirmationBlock status={serverStatus} app={lastSubmittedApp} />
                            </Col>
                          )}
                        </Row>

                        {/* Wizard controls */}
                        {currentStep < 3 && (
                          <div className="d-flex justify-content-between mt-3">
                            <Button
                              type="button"
                              variant="outline-secondary"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                goBack();
                              }}
                              disabled={currentStep === 0}
                            >
                              Previous
                            </Button>

                            {currentStep < 2 ? (
                              <Button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  if (isStepValid(currentStep)) goNext();
                                }}
                                disabled={!isStepValid(currentStep)}
                              >
                                Next
                              </Button>
                            ) : (
                              <Button type="submit" variant="primary" size="lg" disabled={submitting || !valid}>
                                {submitting ? (<><Spinner animation="border" size="sm" className="me-2" /> Submitting...</>) : (<>Get My Quote</>)}
                              </Button>
                            )}
                          </div>
                        )}
                      </>
                    ) : (
                      // VERTICAL VIEW (reuse same sections)
                      <>
                        {!submitOk ? (
                          <>
                            <Row className="g-3">
                              <Col xs={12}>
                                <SectionCard title="Contact & Business">
                                  <SectionContactBusiness
                                    values={{
                                      fullName, email, phone,
                                      businessName, yearsInBusiness, employeesCount, website,
                                      address1, address2, city, state, zipCode,
                                    }}
                                    setters={{
                                      setFullName, setEmail, setPhone,
                                      setBusinessName, setYearsInBusiness, setEmployeesCount, setWebsite,
                                      setAddress1, setAddress2, setCity, setState, setZipCode,
                                    }}
                                    V={V}
                                    vp={vp}
                                    vpOpt={vpOpt}
                                  />
                                </SectionCard>
                              </Col>

                              <Col xs={12}>
                                <SectionCard title="Operations & Exposures">
                                  <SectionOperations
                                    values={{
                                      workEnv, processes, materials, industries, heightWork,
                                      confinedSpaces, usesCuttingTorch, mobileRadiusMiles,
                                      annualRevenue, employeePayroll, subcontractorAnnualCost,
                                    }}
                                    setters={{
                                      setWorkEnv, setProcesses, setMaterials, setIndustries, setHeightWork,
                                      setConfinedSpaces, setUsesCuttingTorch, setMobileRadiusMiles,
                                      setAnnualRevenue, setEmployeePayroll, setSubcontractorAnnualCost,
                                    }}
                                    V={V}
                                    vp={vp}
                                    vpOpt={vpOpt}
                                  />
                                </SectionCard>
                              </Col>

                              <Col xs={12}>
                                <SectionCard title="Coverage & Safety">
                                  {FormSection_SubTitle_CoverageSafety}
                                  <SectionCoverageSafety
                                    values={{
                                      glPerOccurrence, glAggregate, toolsLimit, inlandMarine, commercialAuto, workersComp,
                                      deductible, additionalInsureds, needCOIs,
                                      oshaTraining, ppeEnforced, ventilationControls, hotWorkPermits, fireWatch,
                                      priorClaimsCount5y, lastClaimDate,
                                    }}
                                    setters={{
                                      setGlPerOccurrence, setGlAggregate, setToolsLimit, setInlandMarine, setCommercialAuto, setWorkersComp,
                                      setDeductible, setAdditionalInsureds, setNeedCOIs,
                                      setOshaTraining, setPpeEnforced, setVentilationControls, setHotWorkPermits, setFireWatch,
                                      setPriorClaimsCount5y, setLastClaimDate,
                                    }}
                                    V={V}
                                    vp={vp}
                                    vpOpt={vpOpt}
                                  />
                                </SectionCard>
                              </Col>

                              <Col xs={12}>
                                <SectionCard title="Notes">
                                  <SectionNotesFiles
                                    values={{ notes, fileUrls }}
                                    setters={{ setNotes, setFileUrls }}
                                    V={V}
                                    vpOpt={vpOpt}
                                  />
                                </SectionCard>
                              </Col>
                            </Row>

                            <div className="d-grid mt-3">
                              <Button type="submit" variant="primary" size="lg" disabled={!valid || submitting}>
                                {submitting ? (<><Spinner animation="border" size="sm" className="me-2" /> Submitting...</>) : (<>Get My Quote</>)}
                              </Button>
                              <div className="text-center mt-2 text-body-secondary" style={{ fontSize: "0.9rem" }}>
                                By clicking, you agree we may contact you about welding insurance options. No spam. You can opt out anytime.
                              </div>
                            </div>
                          </>
                        ) : (
                          <ConfirmationBlock status={serverStatus} app={lastSubmittedApp} />
                        )}
                      </>
                    )}
                  </Form>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* How It Works */}
      <section className="py-5 bg-light border-top border-bottom">
        <Container>
          <Row className="mb-4">
            <Col lg={8}>
              <h2 className="fw-bold mb-1">How It Works</h2>
              <p className="text-body-secondary mb-0">Three simple steps to protect your welding business.</p>
            </Col>
          </Row>
          <Row className="g-4">
            <Col md={4}>
              <div className="d-flex align-items-start gap-3">
                <div className="badge rounded-pill text-bg-primary fs-6 mt-1">1</div>
                <div>
                  <h6 className="mb-1">Tell us about your operation</h6>
                  <p className="mb-0 text-body-secondary">Shop/mobile, processes, industries, and exposures.</p>
                </div>
              </div>
            </Col>
            <Col md={4}>
              <div className="d-flex align-items-start gap-3">
                <div className="badge rounded-pill text-bg-primary fs-6 mt-1">2</div>
                <div>
                  <h6 className="mb-1">Pick your coverage</h6>
                  <p className="mb-0 text-body-secondary">GL limits, tools, inland marine, and endorsements.</p>
                  <br></br>
                  <p className="mb-0 text-body-secondary">Call us with questions and/or submit your basic info and we will contact you to help identify the best options for your situation.</p>
                  
                </div>
              </div>
            </Col>
            <Col md={4}>
              <div className="d-flex align-items-start gap-3">
                <div className="badge rounded-pill text-bg-primary fs-6 mt-1">3</div>
                <div>
                  <h6 className="mb-1">We shop carriers</h6>
                  <p className="mb-0 text-body-secondary">We help activate your insurnace and issue Certificates of Insurance (COIs) fast.</p>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* FAQ */}
      {/* <section id="faq" className="py-5 bg-light border-top">
        <Container>
          <Row className="mb-4">
            <Col lg={8}>
              <h2 className="fw-bold mb-1">Frequently Asked Questions</h2>
              <p className="text-body-secondary mb-0">Quick answers about welding insurance.</p>
            </Col>
          </Row>

          <h2>FAQ COMPONENT GOES HERE </h2> */}

          {/* <FAQ_Component_v02b_Simple
            faqs={weldingFAQs.map((f) => ({ question: f.q, answer: f.a }))}
            openFirst
          /> */}
        {/* </Container>
      </section> */}



        {/* FAQ */}
            <section id="faq" className="py-5 bg-light border-top">
              <Container>
                <Row className="mb-4">
                  <Col lg={8}>
                    <h2 className="fw-bold mb-1">Frequently Asked Questions</h2>
                    <p className="text-body-secondary mb-0">Short answers to common concerns.</p>
                  </Col>
                </Row>
      
                <FAQ_Component_v02b_Simple
                  faqs={WELDING_INSURANCE_FAQ_CONTENT_v01}
                  answerClassName="simpleAccordion-Answer"
                  questionHeaderClassName="simpleAaccordion-header-question"
                  openFirst
                />
              </Container>
            </section>

      {/* Footer */}
      <footer className="py-4">
        <Container>
          <Row className="align-items-center">
            <Col md={6} className="small text-body-secondary">
              © {new Date().getFullYear()} Discount Welding Insurance. All rights reserved.
              <div className="mt-2">
                Insurance services subject to licensing and state availability. This page is for general information and not legal advice.
              </div>
            </Col>
            <Col md={6} className="d-flex justify-content-md-end mt-3 mt-md-0 gap-2">
              <Button variant="secondary" size="sm" href="tel:3033375434">Call {AGENT_PHONE_NUMBER}</Button>
            </Col>
          </Row>
        </Container>
      </footer>
    </div>
  );
};

/* ============================================================
   REUSABLE SECTION COMPONENTS
============================================================ */

const SectionCard: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <fieldset className="border rounded-3 p-3 bg-body-secondary">
    <legend className="px-2 fs-6 text-muted" style={{ top: "0.5rem", background: "var(--bs-secondary-bg)" }}>
      <strong>{title}</strong>
    </legend>
    <div className="mt-1">{children}</div>
  </fieldset>
);

/* ---------- Contact & Business ---------- */
const SectionContactBusiness: React.FC<{
  values: {
    fullName: string; email: string; phone: string;
    businessName: string; yearsInBusiness: string; employeesCount: string; website: string;
    address1: string; address2: string; city: string; state: string; zipCode: string;
  };
  setters: {
    setFullName: (v: string) => void; setEmail: (v: string) => void; setPhone: (v: string) => void;
    setBusinessName: (v: string) => void; setYearsInBusiness: (v: string) => void; setEmployeesCount: (v: string) => void; setWebsite: (v: string) => void;
    setAddress1: (v: string) => void; setAddress2: (v: string) => void; setCity: (v: string) => void; setState: (v: string) => void; setZipCode: (v: string) => void;
  };
  V: any;
  vp: (name: string, pass: boolean) => any;
  vpOpt: (name: string, valuePresent: boolean, passIfPresent?: boolean) => any;
}> = ({ values, setters, V, vp, vpOpt }) => {
  const { fullName, email, phone, businessName, yearsInBusiness, employeesCount, website, address1, address2, city, state, zipCode } = values;
  const { setFullName, setEmail, setPhone, setBusinessName, setYearsInBusiness, setEmployeesCount, setWebsite, setAddress1, setAddress2, setCity, setState, setZipCode } = setters;

  return (
    
    
    <Row className="g-3">
      <Col md={6}>
        <Form.Label htmlFor="fullName">Contact - Full Name </Form.Label>
        <Form.Control id="fullName" value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="Jane Doe" required {...vp("fullName", V.fullName)} />
        <Form.Control.Feedback type="invalid">Please enter your full name.</Form.Control.Feedback>
      </Col>
      

      <Col md={6}>
        <Form.Label htmlFor="businessName">Business Name</Form.Label>
        <Form.Control id="businessName" value={businessName} onChange={(e) => setBusinessName(e.target.value)} placeholder="Acme Welding LLC" required {...vp("businessName", V.businessName)} />
      </Col>
      



      <Col md={4}>
        <Form.Label htmlFor="website">Website (optional)</Form.Label>
        <Form.Control id="website" value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="https://example.com" {...vpOpt("website", !!website.trim())} />
      </Col>


      <Col md={4}>
        <Form.Label htmlFor="email">Email</Form.Label>
        <Form.Control id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" required {...vp("email", V.email)} />
        <Form.Control.Feedback type="invalid">Enter a valid email.</Form.Control.Feedback>
      </Col>


      <Col md={4}>
        <Form.Label htmlFor="phone">Phone</Form.Label>
        <Form.Control id="phone" inputMode="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="(555) 123-4567" required {...vp("phone", V.phone)} />
        <Form.Control.Feedback type="invalid">Please enter a phone number.</Form.Control.Feedback>
      </Col>


      
      <Col md={4}>
        <Form.Label htmlFor="yib">Years in Business</Form.Label>
        <Form.Control id="yib" inputMode="numeric" value={yearsInBusiness} onChange={(e) => setYearsInBusiness(e.target.value)} placeholder="0" />
      </Col>

      <Col md={4}>
        <Form.Label htmlFor="emp">Employees</Form.Label>
        <OverlayTrigger placement="right" overlay={<Tooltip id="NumberOfEmployeesTip">Enter the number of employees on W2 payroll.</Tooltip>}>
          <Form.Control id="emp" inputMode="numeric" value={employeesCount} onChange={(e) => setEmployeesCount(e.target.value)} placeholder="0" />
        </OverlayTrigger>
      </Col>


     
      


          <Col md={4}>
            <Form.Label htmlFor="businessType">Business / Entity</Form.Label>
            <Form.Select
              id="businessType"
              // value={businessType}
              // onChange={(e) => setBusinessType(e.target.value as BusinessType)}
              // required
              // {...vp("businessType", V.businessType)}
            >
              <option value="">Select</option>
              <option value="none">No business</option>
              <option value="soleProp">Sole Proprietor</option>
              <option value="llc">LLC</option>
              <option value="sCorp">S-Corp</option>
              <option value="cCorp">C-Corp</option>
              <option value="partnership">Partnership</option>
            </Form.Select>
          </Col>

      {/* 
      
      // <Col md={4}>
      //   <Form.Label htmlFor="businessType"
      //   </Form.Label>
      //   <Form.Select
      //       id="businessType"
      //       // value={businessType}
      //       // onChange={(e) => setBusinessType(e.target.value as BusinessType)}
      //       // required
      //       // {...vp("businessType", V.businessType)}
      //     >
      //     <option value="">Select</option>
      //     <option value="LLC">LLC</option>
      //     <option value="S-Corp">S-Corp</option>
      //     <option value="C-Corp">C-Corp</option>
      //     <option value="Sole Proprietorship">Sole Proprietorship</option>
      //     <option value="Partnership">Partnership</option>
      //     <option value="Other">Other</option>
      //   </Form.Select>
      // </Col> 
       */}
      


      <Col md={12}><hr className="my-2" /></Col>



      <Col md={6}>
        <Form.Label htmlFor="address1">Street Address</Form.Label>
        <Form.Control id="address1" value={address1} onChange={(e) => setAddress1(e.target.value)} placeholder="123 Main St" required {...vp("address1", V.address1)} />
        <Form.Control.Feedback type="invalid">Enter your street address.</Form.Control.Feedback>
      </Col>

      <Col md={4}>
        <Form.Label htmlFor="address2">Unit / Suite</Form.Label>
        <Form.Control id="address2" value={address2} onChange={(e) => setAddress2(e.target.value)} placeholder="Suite 200" {...vpOpt("address2", !!address2.trim())} />
      </Col>

      <Col md={4}>
        <Form.Label htmlFor="city">City</Form.Label>
        <Form.Control id="city" value={city} onChange={(e) => setCity(e.target.value)} placeholder="City" required {...vp("city", V.city)} />
      </Col>
      <Col md={4}>
        <Form.Label htmlFor="state">State</Form.Label>
        <Form.Select id="state" value={state} onChange={(e) => setState(e.target.value)} required {...vp("state", V.state)}>
          <option value="">Select</option>
          {US_STATES.map((s) => <option key={s.code} value={s.code}>{s.name}</option>)}
        </Form.Select>
      </Col>
      <Col md={4}>
        <Form.Label htmlFor="zipCode">Zip</Form.Label>
        <Form.Control id="zipCode" inputMode="numeric" value={zipCode} onChange={(e) => setZipCode(e.target.value)} placeholder="#####" required {...vp("zip", V.zip)} />
        <Form.Control.Feedback type="invalid">Please enter your zip.</Form.Control.Feedback>
      </Col>
    </Row>
  );
};

/* ---------- Operations & Exposures ---------- */
const SectionOperations: React.FC<{
  values: {
    workEnv: WorkEnv; processes: WeldProcess[]; materials: string[]; industries: string[]; heightWork: HeightLevel;
    confinedSpaces: "yes" | "no"; usesCuttingTorch: "yes" | "no"; mobileRadiusMiles: string;
    annualRevenue: string; employeePayroll: string; subcontractorAnnualCost: string;
  };
  setters: {
    setWorkEnv: (v: WorkEnv) => void; setProcesses: (v: WeldProcess[]) => void; setMaterials: (v: string[]) => void; setIndustries: (v: string[]) => void; setHeightWork: (v: HeightLevel) => void;
    setConfinedSpaces: (v: "yes" | "no") => void; setUsesCuttingTorch: (v: "yes" | "no") => void; setMobileRadiusMiles: (v: string) => void;
    setAnnualRevenue: (v: string) => void; setEmployeePayroll: (v: string) => void; setSubcontractorAnnualCost: (v: string) => void;
  };
  V: any;
  vp: (name: string, pass: boolean) => any;
  vpOpt: (name: string, valuePresent: boolean, passIfPresent?: boolean) => any;
}> = ({ values, setters, V, vp, vpOpt }) => {
  const {
    workEnv, processes, materials, industries, heightWork,
    confinedSpaces, usesCuttingTorch, mobileRadiusMiles,
    annualRevenue, employeePayroll, subcontractorAnnualCost,
  } = values;
  const {
    setWorkEnv, setProcesses, setMaterials, setIndustries, setHeightWork,
    setConfinedSpaces, setUsesCuttingTorch, setMobileRadiusMiles,
    setAnnualRevenue, setEmployeePayroll, setSubcontractorAnnualCost,
  } = setters;

  const toggleInArray = <T extends string>(arr: T[], val: T, setFn: (v: T[]) => void) => {
    setFn(arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val]);
  };

  const MATERIAL_OPTIONS = ["steel", "stainless", "aluminum", "cast_iron", "copper", "exotic_alloys", "other"];
  const INDUSTRY_OPTIONS = ["construction", "oil_gas", "manufacturing", "marine", "agriculture", "automotive", "art_sculpture", "other"];

  return (
    <Row className="g-3">
      <Col md={12}>
        <Form.Label htmlFor="workEnv">Work Environment</Form.Label>
        <Form.Select id="workEnv" value={workEnv} onChange={(e) => setWorkEnv(e.target.value as WorkEnv)} required {...vp("workEnv", V.workEnv)}>
          <option value="">Select</option>
          <option value="shop">Shop</option>
          <option value="mobile">Mobile</option>
          <option value="both">Both</option>
        </Form.Select>
      </Col>

      <Col md={4}>
        <Form.Label>Welding Processes (select all that apply)</Form.Label>
        <div className="d-flex flex-wrap gap-2">
          {(Object.keys(PROCESS_LABELS) as WeldProcess[]).map((p) => (
            <Form.Check
              key={p}
              type="checkbox"
              id={`proc-${p}`}
              label={PROCESS_LABELS[p]}
              checked={processes.includes(p)}
              onChange={() => toggleInArray(processes, p, setProcesses)}
            />
          ))}
        </div>
        <div className={V.processes ? "form-text" : "text-danger small"}>{V.processes ? " " : "Select at least one process."}</div>
      </Col>

      <Col md={4}>
        <Form.Label>Materials (select all that apply)</Form.Label>
        <div className="d-flex flex-wrap gap-2">
          {MATERIAL_OPTIONS.map((m) => (
            <Form.Check
              key={m}
              type="checkbox"
              id={`mat-${m}`}
              label={m.replace("_", " ")}
              checked={materials.includes(m)}
              onChange={() => toggleInArray(materials, m, setMaterials)}
            />
          ))}
        </div>
        <div className={V.materials ? "form-text" : "text-danger small"}>{V.materials ? " " : "Select at least one material."}</div>
      </Col>

      <Col md={4}>
        <Form.Label>Industries Served<br/>(select all that apply)</Form.Label>
        <div className="d-flex flex-wrap gap-2">
          {INDUSTRY_OPTIONS.map((i) => (
            <Form.Check
              key={i}
              type="checkbox"
              id={`ind-${i}`}
              label={i.replace("_", " ")}
              checked={industries.includes(i)}
              onChange={() => toggleInArray(industries, i, setIndustries)}
            />
          ))}
        </div>
        <div className={V.industries ? "form-text" : "text-danger small"}>{V.industries ? " " : "Select at least one industry."}</div>
      </Col>

      <Col md={4}>
        <Form.Label htmlFor="heightWork">Work at Height</Form.Label>
        <Form.Select id="heightWork" value={heightWork} onChange={(e) => setHeightWork(e.target.value as HeightLevel)} required {...vp("heightWork", V.heightWork)}>
          <option value="">Select</option>
          <option value="ground_only">Ground only</option>
          <option value="up_to_two_stories">Up to 2 stories</option>
          <option value="over_two_stories">Over 2 stories</option>
        </Form.Select>
      </Col>

      <Col md={4} className="pt-md-4">
        <div className="d-flex flex-wrap gap-3">
          <Form.Check id="confinedSpaces" type="checkbox" label="Confined spaces" checked={confinedSpaces === "yes"} onChange={(e) => setConfinedSpaces(e.currentTarget.checked ? "yes" : "no")} />
          <Form.Check id="torch" type="checkbox" label="Cutting/torch work" checked={usesCuttingTorch === "yes"} onChange={(e) => setUsesCuttingTorch(e.currentTarget.checked ? "yes" : "no")} />
        </div>
      </Col>

      <Col md={4}>
        <Form.Label htmlFor="mobileRadius">Mobile Radius</Form.Label>
        <Form.Control id="mobileRadius" inputMode="numeric" value={mobileRadiusMiles} onChange={(e) => setMobileRadiusMiles(e.target.value)} placeholder="e.g., 50 miles" {...vpOpt("mobileRadius", !!mobileRadiusMiles)} />
        <p>(miles, optional)</p>
      </Col>

      <Col md={4}>
        <Form.Label htmlFor="revenue">Annual<br></br>Revenue</Form.Label>
        <InputGroup hasValidation>
          <InputGroup.Text>$</InputGroup.Text>
          <Form.Control id="revenue" inputMode="numeric" value={annualRevenue} onChange={(e) => setAnnualRevenue(e.target.value)} placeholder="e.g., 150000" required {...vp("annualRevenue", V.annualRevenue)} />
        </InputGroup>
      </Col>

      <Col md={4}>
        <Form.Label htmlFor="payroll">Annual<br></br>Employee Payroll</Form.Label>
        <InputGroup hasValidation>
          <InputGroup.Text>$</InputGroup.Text>
          <Form.Control id="payroll" inputMode="numeric" value={employeePayroll} onChange={(e) => setEmployeePayroll(e.target.value)} placeholder="e.g., 80000" required {...vp("employeePayroll", V.employeePayroll)} />
        </InputGroup>
      </Col>

      <Col md={4}>
        <Form.Label htmlFor="subsCost">Subcontractor<br></br>Annual Cost</Form.Label>
        <InputGroup hasValidation>
          <InputGroup.Text>$</InputGroup.Text>
          <Form.Control
            id="subsCost"
            inputMode="numeric"
            value={subcontractorAnnualCost}
            onChange={(e) => setSubcontractorAnnualCost(e.target.value)}
            placeholder="e.g., 25000"
            {...vpOpt("subcontractorAnnualCost", subcontractorAnnualCost.trim().length > 0)}
          />
        </InputGroup>
        <div className="form-text">Enter total annual dollars paid to subs (optional).</div>
      </Col>
    </Row>
  );
};

/* ---------- Coverage & Safety ---------- */
const SectionCoverageSafety: React.FC<{
  values: {
    glPerOccurrence: string; glAggregate: string; toolsLimit: string; inlandMarine: "yes" | "no"; commercialAuto: "yes" | "no"; workersComp: "yes" | "no";
    deductible: Deductible; additionalInsureds: string; needCOIs: "yes" | "no";
    oshaTraining: "yes" | "no"; ppeEnforced: "yes" | "no"; ventilationControls: "yes" | "no"; hotWorkPermits: "yes" | "no"; fireWatch: "yes" | "no";
    priorClaimsCount5y: string; lastClaimDate: string;
  };
  setters: {
    setGlPerOccurrence: (v: string) => void; setGlAggregate: (v: string) => void; setToolsLimit: (v: string) => void; setInlandMarine: (v: "yes" | "no") => void; setCommercialAuto: (v: "yes" | "no") => void; setWorkersComp: (v: "yes" | "no") => void;
    setDeductible: (v: Deductible) => void; setAdditionalInsureds: (v: string) => void; setNeedCOIs: (v: "yes" | "no") => void;
    setOshaTraining: (v: "yes" | "no") => void; setPpeEnforced: (v: "yes" | "no") => void; setVentilationControls: (v: "yes" | "no") => void; setHotWorkPermits: (v: "yes" | "no") => void; setFireWatch: (v: "yes" | "no") => void;
    setPriorClaimsCount5y: (v: string) => void; setLastClaimDate: (v: string) => void;
  };
  V: any;
  vp: (name: string, pass: boolean) => any;
  vpOpt: (name: string, valuePresent: boolean, passIfPresent?: boolean) => any;
}> = ({ values, setters, V, vp, vpOpt }) => {
  const {
    glPerOccurrence, glAggregate, toolsLimit, inlandMarine, commercialAuto, workersComp,
    deductible, additionalInsureds, needCOIs,
    oshaTraining, ppeEnforced, ventilationControls, hotWorkPermits, fireWatch,
    priorClaimsCount5y, lastClaimDate,
  } = values;
  const {
    setGlPerOccurrence, setGlAggregate, setToolsLimit, setInlandMarine, setCommercialAuto, setWorkersComp,
    setDeductible, setAdditionalInsureds, setNeedCOIs,
    setOshaTraining, setPpeEnforced, setVentilationControls, setHotWorkPermits, setFireWatch,
    setPriorClaimsCount5y, setLastClaimDate,
  } = setters;

  return (
    <Row className="g-3">
      <Col md={6}>
        <Form.Label htmlFor="glOcc">GL Per-Occurrence Limit</Form.Label>
        <Form.Select id="glOcc" value={glPerOccurrence} onChange={(e) => setGlPerOccurrence(e.target.value)} required {...vp("glPerOccurrence", V.glPerOccurrence)}>
          <option value="500000">$500,000</option>
          <option value="1000000">$1,000,000</option>
          <option value="2000000">$2,000,000</option>
        </Form.Select>
      </Col>
      <Col md={6}>
        <Form.Label htmlFor="glAgg">GL Aggregate</Form.Label>
        <Form.Select id="glAgg" value={glAggregate} onChange={(e) => setGlAggregate(e.target.value)} required {...vp("glAggregate", V.glAggregate)}>
          <option value="1000000">$1,000,000</option>
          <option value="2000000">$2,000,000</option>
          <option value="4000000">$4,000,000</option>
        </Form.Select>
      </Col>
      <Col md={6}>
        <Form.Label htmlFor="toolsLimit">Tools & Equipment Limit</Form.Label>
        <InputGroup hasValidation>
          <InputGroup.Text>$</InputGroup.Text>
          <Form.Control id="toolsLimit" inputMode="numeric" value={toolsLimit} onChange={(e) => setToolsLimit(e.target.value)} placeholder="e.g., 10000" required {...vp("toolsLimit", V.toolsLimit)} />
        </InputGroup>
      </Col>
      <Col md={6}>
        <Form.Label htmlFor="ded">Deductible</Form.Label>
        <Form.Select id="ded" value={deductible} onChange={(e) => setDeductible(e.target.value as Deductible)} required {...vp("deductible", V.deductible)}>
          <option value="">Select</option>
          <option value="0">$0</option>
          <option value="250">$250</option>
          <option value="500">$500</option>
          <option value="1000">$1,000</option>
          <option value="2500">$2,500</option>
        </Form.Select>
      </Col>

      <Col md={12} className="pt-2">
        <div className="d-flex flex-wrap gap-3">
          <Form.Check id="inland" type="checkbox" label="Inland Marine (tools/floaters)" checked={inlandMarine === "yes"} onChange={(e) => setInlandMarine(e.currentTarget.checked ? "yes" : "no")} />
          <Form.Check id="auto" type="checkbox" label="Commercial Auto" checked={commercialAuto === "yes"} onChange={(e) => setCommercialAuto(e.currentTarget.checked ? "yes" : "no")} />
          <Form.Check id="wc" type="checkbox" label="Workers' Comp" checked={workersComp === "yes"} onChange={(e) => setWorkersComp(e.currentTarget.checked ? "yes" : "no")} />
          <Form.Check id="cois" type="checkbox" label="Need COIs / AI endorsements" checked={needCOIs === "yes"} onChange={(e) => setNeedCOIs(e.currentTarget.checked ? "yes" : "no")} />
        </div>
      </Col>

      {/* 
      <Col md={4}>
        <Form.Label htmlFor="aiCount">Additional Insureds</Form.Label>
        <Form.Control id="aiCount" inputMode="numeric" value={additionalInsureds} onChange={(e) => setAdditionalInsureds(e.target.value)} placeholder="0" required {...vp("additionalInsureds", V.additionalInsureds)} />
      </Col> 
      */}

      <Col md={12}><hr className="my-2" /></Col>

      <Col md={12}>
        <Form.Label>Safety Practices</Form.Label>
        <div className="d-flex flex-wrap gap-3 pt-1">
          <Form.Check id="osha" type="checkbox" label="OSHA 10/30 training" checked={oshaTraining === "yes"} onChange={(e) => setOshaTraining(e.currentTarget.checked ? "yes" : "no")} />
          <Form.Check id="ppe" type="checkbox" label="PPE enforced" checked={ppeEnforced === "yes"} onChange={(e) => setPpeEnforced(e.currentTarget.checked ? "yes" : "no")} />
          <Form.Check id="vent" type="checkbox" label="Ventilation controls" checked={ventilationControls === "yes"} onChange={(e) => setVentilationControls(e.currentTarget.checked ? "yes" : "no")} />
          <Form.Check id="hwp" type="checkbox" label="Hot work permits" checked={hotWorkPermits === "yes"} onChange={(e) => setHotWorkPermits(e.currentTarget.checked ? "yes" : "no")} />
          <Form.Check id="fw" type="checkbox" label="Fire watch on hot work" checked={fireWatch === "yes"} onChange={(e) => setFireWatch(e.currentTarget.checked ? "yes" : "no")} />
        </div>
      </Col>

      <Col md={4}>
        <Form.Label htmlFor="claims">Prior Claims (5y)</Form.Label>
        <Form.Control id="claims" inputMode="numeric" value={priorClaimsCount5y} onChange={(e) => setPriorClaimsCount5y(e.target.value)} placeholder="0" required {...vp("priorClaimsCount5y", V.priorClaimsCount5y)} />
      </Col>

      <Col md={4}>
        <Form.Label htmlFor="lastClaim">Last Claim Date </Form.Label>
        <Form.Control id="lastClaim" type="date" value={lastClaimDate} onChange={(e) => setLastClaimDate(e.target.value)} {...vpOpt("lastClaimDate", !!lastClaimDate, true)} />
        <p>(optional)</p>
      </Col>
    </Row>
  );
};

/* ---------- Notes / Files ---------- */
const SectionNotesFiles: React.FC<{
  values: { notes: string; fileUrls: string };
  setters: { setNotes: (v: string) => void; setFileUrls: (v: string) => void };
  V: any;
  vpOpt: (name: string, valuePresent: boolean, passIfPresent?: boolean) => any;
}> = ({ values, setters, vpOpt }) => {
  const { notes, fileUrls } = values;
  const { setNotes, setFileUrls } = setters;

  return (
    <Row className="g-3">
      <Col md={12}>
        <Form.Label htmlFor="notes">Additional Notes (optional)</Form.Label>
        <Form.Control id="notes" as="textarea" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Project types, certifications, special hazards, etc." {...vpOpt("notes", !!notes.trim())} />
      </Col>


      {/* <Col md={12}>
        <Form.Label htmlFor="fileUrls">File/Photo URLs (optional)</Form.Label>
        <Form.Control id="fileUrls" as="textarea" rows={2} value={fileUrls} onChange={(e) => setFileUrls(e.target.value)} placeholder="One URL per line (photos, COI requests, schedules, etc.)" {...vpOpt("fileUrls", !!fileUrls.trim())} />
        <div className="form-text">Tip: share public links so advisors can review them.</div>
      </Col>
     */}
    
    </Row>
  );
};

/* ============================================================
   Confirmation block (Sectioned, readable, no JSON)
============================================================ */
const ConfirmationBlock: React.FC<{
  status: { ok: boolean; status?: number; id?: string; message?: string } | null;
  app: ILeadPayload | null;
}> = ({ status, app }) => {
  const ok = !!status?.ok;

  if (!app) {
    return (
      <Card className="border-0">
        <Card.Body className="p-4">
          <Alert variant="warning" className="mb-0">We don’t have your details to display yet.</Alert>
        </Card.Body>
      </Card>
    );
  }

  const listify = (arr?: string[] | WeldProcess[]) => (arr && arr.length ? arr.map((x) => String(x).replace(/_/g, " ")).join(", ") : "—");

  return (
    <Card className="border-0">
      <Card.Body className="p-4">
        {ok ? (
          <Alert variant="success" className="d-flex align-items-start">
            <div>
              <div className="fw-bold mb-1">Thank you! A licensed advisor will contact you shortly.</div>
              <div className="small">
                Submission status: <strong>{status?.status ?? 200}</strong>
                {status?.id ? <> • Reference ID: <strong>{status.id}</strong></> : null}
              </div>
            </div>
          </Alert>
        ) : (
          <Alert variant="warning" className="d-flex align-items-start">
            <div>
              <div className="fw-bold mb-1">We couldn’t confirm your submission.</div>
              <div className="small">{status?.message || "Please try again or give us a call."}</div>
            </div>
          </Alert>
        )}




        <div className="mt-3">
          <Section title="Contact & Business">
            <KV label="Name" value={dash(app.fullName)} />
            <KV label="Email" value={dash(app.email)} />
            <KV label="Phone" value={dash(app.phone)} />
            <KV label="Business" value={dash(app.businessName)} />
            <KV label="Years in Business" value={dash(app.yearsInBusiness)} />
            <KV label="Employees" value={dash(app.employeesCount)} />
            <KV label="Website" value={dash(app.website)} />
          </Section>

          <Section title="Address">
            <KV label="Street" value={dash(app.address1)} />
            <KV label="Unit / Suite" value={dash(app.address2)} />
            <KV label="City / State / Zip" value={`${dash(app.city)}, ${dash(app.state)} ${dash(app.zipCode)}`} />
          </Section>

          <Section title="Operations & Exposures">
            <KV label="Environment" value={dash(app.workEnv)} />
            <KV label="Processes" value={listify(app.processes)} />
            <KV label="Materials" value={listify(app.materials)} />
            <KV label="Industries" value={listify(app.industries)} />
            <KV label="Height Work" value={dash(app.heightWork)} />
            <KV label="Confined Spaces" value={dash(app.confinedSpaces)} />
            <KV label="Cutting/Torch" value={dash(app.usesCuttingTorch)} />
            <KV label="Mobile Radius" value={app.mobileRadiusMiles ? `${app.mobileRadiusMiles} miles` : "—"} />
            <KV label="Annual Revenue" value={money(app.annualRevenue)} />
            <KV label="Employee Payroll" value={money(app.employeePayroll)} />
            <KV label="Subcontractor Annual Cost" value={money(app.subcontractorAnnualCost)} />
          </Section>

          <Section title="Coverage & Safety">
            <KV label="GL Per-Occurrence" value={money(app.glPerOccurrence)} />
            <KV label="GL Aggregate" value={money(app.glAggregate)} />
            <KV label="Tools & Equipment" value={money(app.toolsLimit)} />
            <KV label="Inland Marine" value={dash(app.inlandMarine)} />
            <KV label="Commercial Auto" value={dash(app.commercialAuto)} />
            <KV label="Workers' Comp" value={dash(app.workersComp)} />
            <KV label="Deductible" value={app.deductible ? money(app.deductible) : "—"} />
            <KV label="Additional Insureds" value={dash(app.additionalInsureds)} />
            <KV label="COIs Needed" value={dash(app.needCOIs)} />
            <KV label="OSHA Training" value={dash(app.oshaTraining)} />
            <KV label="PPE Enforced" value={dash(app.ppeEnforced)} />
            <KV label="Ventilation Controls" value={dash(app.ventilationControls)} />
            <KV label="Hot Work Permits" value={dash(app.hotWorkPermits)} />
            <KV label="Fire Watch" value={dash(app.fireWatch)} />
            <KV label="Prior Claims (5y)" value={dash(app.priorClaimsCount5y)} />
            <KV label="Last Claim Date" value={dash(app.lastClaimDate)} />
          </Section>

          <Section title="Notes / Files">
            <div className="p-3 bg-body-tertiary border rounded mb-2">{dash(app.notes)}</div>
            <KV label="File/Photo URLs" value={dash(app.fileUrls)} />
          </Section>
        </div>

        <div className="mt-4 d-flex flex-wrap gap-2">
          <Button href="tel:3033375434" variant="primary">Call {AGENT_PHONE_NUMBER}</Button>
          <Button variant="outline-secondary" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>Back to top</Button>
          {/* <Button variant="outline-secondary" onClick={() => window.print()}>Print</Button> */}
        </div>
      </Card.Body>
    </Card>
  );
};





/* ---------- Tiny helpers for confirmation layout ---------- */
const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="mb-4">
    <h6 className="text-uppercase text-muted mb-2">{title}</h6>
    <Row as="dl" className="row-cols-1 row-cols-md-2 g-3 mb-0">{children}</Row>
    <hr className="mt-3 mb-0" />
  </div>
);

const KV: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Col className="mb-0">
    <dt className="small text-muted mb-1">{label}</dt>
    <dd className="mb-0">{value}</dd>
  </Col>
);

export default DISCOUNT_WELDING_LandingPage_v020b_WIZARD;
